import { createTheme } from "@mui/material/styles";

// OT App design system — calm cyan + health green, friendly rounded type.
// Used by every @mui/material (v5) component via the ThemeProvider in App.js.

export const palette = {
  primary: "#0891B2",
  primaryDark: "#0E7490",
  primaryLight: "#22D3EE",
  primarySoft: "#E0F7FA",
  cta: "#059669",
  ctaDark: "#047857",
  bg: "#F4F9FA",
  paper: "#FFFFFF",
  text: "#164E63",
  textSecondary: "#475569",
  border: "#E2E8F0",
  danger: "#DC2626",
};

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: palette.primary, dark: palette.primaryDark, light: palette.primaryLight },
    secondary: { main: palette.cta, dark: palette.ctaDark },
    success: { main: palette.cta },
    error: { main: palette.danger },
    background: { default: palette.bg, paper: palette.paper },
    text: { primary: palette.text, secondary: palette.textSecondary },
    divider: palette.border,
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: '"Nunito Sans", "Helvetica Neue", Arial, sans-serif',
    h1: { fontFamily: '"Nunito", "Nunito Sans", sans-serif', fontWeight: 800 },
    h2: { fontFamily: '"Nunito", "Nunito Sans", sans-serif', fontWeight: 800 },
    h3: { fontFamily: '"Nunito", "Nunito Sans", sans-serif', fontWeight: 800 },
    h4: { fontFamily: '"Nunito", "Nunito Sans", sans-serif', fontWeight: 800, fontSize: "1.75rem" },
    h5: { fontFamily: '"Nunito", "Nunito Sans", sans-serif', fontWeight: 800, fontSize: "1.35rem" },
    h6: { fontFamily: '"Nunito", "Nunito Sans", sans-serif', fontWeight: 700, fontSize: "1.1rem" },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 700 },
    button: { textTransform: "none", fontWeight: 700 },
    body1: { fontSize: "1rem", lineHeight: 1.6 },
    body2: { lineHeight: 1.55 },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          paddingLeft: 18,
          paddingRight: 18,
          minHeight: 44,
          transition: "background-color 200ms ease, border-color 200ms ease, color 200ms ease",
        },
        containedPrimary: {
          "&:hover": { backgroundColor: palette.primaryDark },
        },
        containedSecondary: {
          color: "#FFFFFF",
          "&:hover": { backgroundColor: palette.ctaDark },
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${palette.border}`,
          boxShadow: "0 1px 3px rgba(15, 42, 51, 0.06)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 16 },
      },
    },
    MuiTextField: {
      defaultProps: { variant: "outlined" },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: "#FFFFFF",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 700 },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: 10 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { fontSize: "0.8rem", fontWeight: 600 },
      },
    },
  },
});

// Shared sx for @mui/x-data-grid tables so every list page looks identical.
export const dataGridSx = {
  border: `1px solid ${palette.border}`,
  borderRadius: "16px",
  backgroundColor: "#FFFFFF",
  fontSize: "0.95rem",
  color: palette.text,
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: palette.primarySoft,
    borderBottom: `1px solid ${palette.border}`,
    borderRadius: "16px 16px 0 0",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 800,
    color: palette.text,
  },
  "& .MuiDataGrid-row": {
    cursor: "pointer",
    transition: "background-color 150ms ease",
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: "#F0FAFC",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: `1px solid #EEF2F6`,
  },
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
    outline: "none",
  },
  "& .MuiDataGrid-cell:focus-visible": {
    outline: `2px solid ${palette.primary}`,
    outlineOffset: "-2px",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: `1px solid ${palette.border}`,
  },
};

export default theme;
